import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import translate from '../../../../commonComponents/translate';
import TrophyPortlet from './TrophyPortlet';
import {
  formatNumbers,
  newFormatPrice,
  TROPHY_CONFIG,
} from '../../../../services/utilityFunctions';
import TrophyModal from './TrophyModal';
import performanceProp from '../../propTypes/performanceProp';
import chartsProp from '../../propTypes/chartsProp';
import ServicesContext from '../../../../contextProviders/ServicesContext';
import CertificatePortlet from './CertificatePortlet';
import certificateProp from '../../../academy/propTypes/certificateProp';
import trophyProp from '../../../journey/propTypes/trophyProp';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import BasicPortletWithFilters from '../../../../commonComponents/portlets/BasicPortletWithFilters';
import AmColumnAndLineChart from '../../../../commonComponents/charts/AmColumnAndLineChart';
import ProgressChartHalfMoon from './ProgressChartHalfMoon';
import Loader from '../../../../services/loader';
import Notifications from '../../../../services/notifications';
import MyRatings from './MyRatings';
import BasicPortlet from '../../../../commonComponents/portlets/BasicPortlet';
import MyResults from './MyResults';
import Select from '../../../../commonComponents/forms/inputs/Select';
import DateRange from '../../../../commonComponents/forms/inputs/DateRange';
import { useModal } from '../../../../services/customHooks';

const setCommonTrophyConfig = statsParam => {
  const stats = statsParam || { trophies: { types: {} } };

  return Object.entries(TROPHY_CONFIG)
    .filter(([name]) => !name.includes('e-learning_') && name !== 'custom')
    .map(([name, trophy]) => ({
      ...trophy,
      name,
      stats: stats.trophies.types[name],
    }));
};

const setCustomTrophyConfig = (customTrophies, statsParam) => {
  const stats = statsParam || { trophies: { types: {} } };
  return customTrophies.map(t => ({
    _id: t._id,
    translationKey: t.name,
    imageURL: t.icon.url,
    stats: stats.trophies.types.custom[t._id],
  }));
};

// Gets and the unity type
const getUnity = (unity, t) => {
  let infoUnity = '';
  if (unity === 'points') {
    infoUnity = 'pts';
  } else if (unity === 'percentage') {
    infoUnity = '%';
  } else if (unity === 'VA') {
    infoUnity = t('challengeDetails_leaderboard_VA');
  }
  return infoUnity;
};

// WEEK - MONTH - YEAR, VALUE
const weekValue = {
  startDate: moment().utc().subtract(7, 'days').valueOf(),
  endDate: moment().valueOf(),
};
const monthValue = {
  startDate: moment().utc().subtract(1, 'month').valueOf(),
  endDate: moment().valueOf(),
};
const yearValue = {
  startDate: moment().utc().subtract(1, 'year').valueOf(),
  endDate: moment().valueOf(),
};

const getColors = typeGraph => {
  if (typeGraph === 'points') {
    return { mainColor: '#1CACF4', secondaryColor: '#0080bc' };
  }
  if (typeGraph === 'currency') {
    return { mainColor: '#E69138', secondaryColor: '#b8742c' };
  }
  if (typeGraph === 'percentage') {
    return { mainColor: '#8fce00', secondaryColor: '#72a400' };
  }
  if (typeGraph === 'VA') {
    return { mainColor: '#674ea7', secondaryColor: '#523e85' };
  }
  return null;
};

function TabPerformPerformance({ t, stats, certificates, customTrophies }) {
  // Loader
  const loader = useRef(new Loader('#your-perform'));
  const [isLoading, setLoading] = useState(false);
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);
  /** INITIAL STATE */
  const { loggedUser } = useContext(LoggedUserContext);
  // Charts
  const [charts, setCharts] = useState({});
  const [filterActive, setFilterActive] = useState('week');
  const graphFilterList = [
    {
      id: 'week',
      text: t('perform.performance.filter.groupPerDay'),
    },
    {
      id: 'month',
      text: t('perform.performance.filter.groupPerWeek'),
    },
    {
      id: 'year',
      text: t('perform.performance.filter.groupPerYear'),
    },
  ];
  const initialFiltersState = {
    startDate: moment().utc().subtract(1, 'month').valueOf(),
    endDate: moment().valueOf(),
  };
  const [filters, setFilters] = useState(initialFiltersState);
  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({
      ...prevState,
      startDate,
      endDate,
    }));
  };
  /** SHOW ONLY THE FEATURES ENABLED */
  const services = useContext(ServicesContext);
  const connectIsAvailable =
    services && services.connect && services.connect.enabled === true;
  const performIsAvailable =
    services && services.perform && services.perform.enabled === true;
  const certificatesIsAvailable =
    services && services.certificates && services.certificates.enabled === true;
  /** TROPHYS */
  // Trophy modal
  const [modalState, setModalState] = useModal({
    show: false,
    data: null,
  });
  const showModal = trophy => setModalState({ show: true, data: trophy });
  // GIVEN TROPHY
  const commonTrophyConfig = setCommonTrophyConfig(stats.performance);
  const customTrophyConfig = setCustomTrophyConfig(
    customTrophies,
    stats.performance,
  );
  const commonSectionConfig = commonTrophyConfig.concat(customTrophyConfig);
  const hideModal = () => setModalState({ show: false, data: null });
  const getTrophies = () => {
    const trophies = stats.charts.trophies || [];
    const skillTrophies = stats.charts.skillsTrophies || [];
    const allTrophies = trophies.concat(skillTrophies);
    return modalState.data && modalState.data.name
      ? allTrophies.filter(tr => tr.type === modalState.data.name)
      : allTrophies.filter(
          tr =>
            tr.type === 'custom' &&
            tr.trophyCustom &&
            tr.trophyCustom._id === modalState.data._id,
        );
  };

  /** API REQUESTS */
  const getChallengesPerUnity = () => {
    setLoading(true);
    let params = {};
    if (filterActive === 'week') {
      params = weekValue;
    }
    if (filterActive === 'month') {
      params = monthValue;
    }
    if (filterActive === 'year') {
      params = yearValue;
    }
    if (filters.startDate) {
      params.startDate = filters.startDate;
    }
    if (filters.endDate) {
      params.endDate = filters.endDate;
    }
    axios
      .get(`/api/user/${loggedUser._id}/performance/challenges-unity`, {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data: response }) => {
        setCharts(prevState => ({
          ...prevState,
          points: response.data.find(p => p.unity === 'points').performance,
          percentage: response.data.find(p => p.unity === 'percentage')
            .performance,
          currency: response.data.find(p => p.unity === 'currency').performance,
          VA: response.data.find(p => p.unity === 'VA').performance,
        }));
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(
          err,
          'profile.performance.error.yourPerformance',
        );
      });
  };

  useEffect(() => {
    getChallengesPerUnity();
  }, [filterActive, filters]); // Request when user changes the filter (Week, Month, Year)

  /** CHARTS */
  const getCharts = (type, typeGraph) => {
    const colors = getColors(typeGraph);
    if (type === 'yourPerformance') {
      const statsChart = charts[typeGraph].charts;
      let updatedChart = [];
      let category = 'daySlashMonth';
      const isCurrency = typeGraph === 'currency';
      const infoUnity = getUnity(typeGraph, t);

      if (filterActive === 'week') {
        updatedChart = statsChart.map(w => ({
          ...w,
          daySlashMonth: `${w.day}/${w.month}`,
          userPoints: isCurrency
            ? newFormatPrice(w.userPoints, false, w.currency)
            : `${formatNumbers(w.userPoints)} ${infoUnity}`,
          teamPoints: isCurrency
            ? newFormatPrice(w.teamPoints, false, w.currency)
            : `${formatNumbers(w.teamPoints)} ${infoUnity}`,
          userPointsAverage: isCurrency
            ? newFormatPrice(w.userPointsAverage, false, w.currency)
            : `${formatNumbers(w.userPointsAverage)} ${infoUnity}`,
        }));
      }
      if (filterActive === 'month') {
        category = 'monthString';
        updatedChart = statsChart.map(y => ({
          ...y,
          monthString: `${y.week}-${y.month}`,
          userPoints: isCurrency
            ? newFormatPrice(y.userPoints, false, y.currency)
            : `${formatNumbers(y.userPoints)} ${infoUnity}`,
          teamPoints: isCurrency
            ? newFormatPrice(y.teamPoints, false, y.currency)
            : `${formatNumbers(y.teamPoints)} ${infoUnity}`,
          userPointsAverage: isCurrency
            ? newFormatPrice(y.userPointsAverage, false, y.currency)
            : `${formatNumbers(y.userPointsAverage)} ${infoUnity}`,
        }));
      }
      if (filterActive === 'year') {
        category = 'monthString';
        updatedChart = statsChart.map(y => ({
          ...y,
          monthString: moment()
            .month(y.month - 1)
            .format('MMM'),
          userPoints: isCurrency
            ? newFormatPrice(y.userPoints, false, y.currency)
            : `${formatNumbers(y.userPoints)} ${infoUnity}`,
          teamPoints: isCurrency
            ? newFormatPrice(y.teamPoints, false, y.currency)
            : `${formatNumbers(y.teamPoints)} ${infoUnity}`,
          userPointsAverage: isCurrency
            ? newFormatPrice(y.userPointsAverage, false, y.currency)
            : `${formatNumbers(y.userPointsAverage)} ${infoUnity}`,
        }));
      }
      return (
        <AmColumnAndLineChart
          containerClass="report-chart-content"
          options={{
            category,
            yLabel: '', // Y TEXT,
            column2ValueKey: 'userPoints',
            column2SeriesName: t('perform.performance.label.yourScore'),
            columnValueKey: 'teamPoints',
            columnSeriesName: t('perform.performance.label.teamScore'),
            lineValueKey: 'userPointsAverage',
            lineSeriesName: t('perform.performance.label.yourAverage'),
            colorRect2: colors.secondaryColor,
            colorLine: '#F5D509',
            colorRect: colors.mainColor,
            closerColumns: false,
          }}
          chartId={typeGraph}
          stats={updatedChart}
          type="layeredColumns"
        />
      );
    }
    if (type === 'progress') {
      const statsTotals = charts[typeGraph].totals;
      return (
        <div className="row">
          <div className="col-lg-6 col-12">
            <ProgressChartHalfMoon
              totalProgress={statsTotals.totalProgress}
              totalScoreTarget={statsTotals.totalScoreTarget}
              totalUserPoints={statsTotals.totalUserPoints}
              unity={typeGraph}
              color={colors.mainColor}
              unityType={getUnity(typeGraph, t)}
              statsObj={charts[typeGraph]}
            />
          </div>
          <div className="col-lg-6 col-12">
            <ProgressChartHalfMoon
              totalProgress={statsTotals.completionRate}
              unity="percentage"
              color={colors.mainColor}
            />
          </div>
        </div>
      );
    }
    return null;
  };
  const renderFilter = type => (
    <div id="customHeadTools">
      <div className="select-container">
        <Select
          id={`dropdown-${type}`}
          items={graphFilterList}
          value={filterActive}
          onChange={value => {
            setFilterActive(value);
          }}
          searchable={false}
        />
      </div>
      <DateRange
        id="filterDate"
        startDate={filters.startDate}
        endDate={filters.endDate}
        placeholder={t('commissions.filter_date_placeholder')}
        onChange={({ startDate, endDate }) => {
          handleDateChange(startDate, endDate);
        }}
        onClear={() => {
          handleDateChange(
            initialFiltersState.startDate,
            initialFiltersState.endDate,
          );
        }}
        required={false}
      />
    </div>
  );

  return (
    <>
      {modalState.show && (
        <TrophyModal trophies={getTrophies()} onClose={hideModal} />
      )}
      <div className="tab-perform-performance">
        <div id="my-ratings">
          <BasicPortlet title={t('profile.performance.myRatings.title')}>
            <MyRatings owner={loggedUser._id} />
          </BasicPortlet>
        </div>
        <div id="my-results">
          <BasicPortlet title={t('profile.performance.myResults.title')}>
            <MyResults owner={loggedUser._id} />
          </BasicPortlet>
        </div>
        {certificatesIsAvailable && (
          <CertificatePortlet certificates={certificates} />
        )}
        {(connectIsAvailable || performIsAvailable) && (
          <TrophyPortlet
            title={t('profile_givenTrophies')}
            trophyConfig={commonSectionConfig}
            onSelect={showModal}
          />
        )}
        {performIsAvailable && (
          <div id="your-perform">
            <BasicPortletWithFilters
              title={t('profile.performance.yourPerformance.title')}
              customDropdown
              customFiltersChildren={renderFilter('performance')}
            >
              {charts ? (
                <div>
                  {charts?.points && (
                    <div>
                      {(charts?.points?.totals?.completionRate ||
                        charts?.points?.totals?.totalProgress) &&
                        getCharts('progress', 'points')}
                      {charts?.points?.charts?.length > 0 &&
                        getCharts('yourPerformance', 'points')}
                    </div>
                  )}
                  {charts?.currency && (
                    <div>
                      {(charts?.currency?.totals?.completionRate ||
                        charts?.currency?.totals?.totalProgress) &&
                        getCharts('progress', 'currency')}
                      {charts?.currency?.charts?.length > 0 &&
                        getCharts('yourPerformance', 'currency')}
                    </div>
                  )}
                  {charts?.percentage && (
                    <div>
                      {(charts?.percentage?.totals?.completionRate ||
                        charts?.percentage?.totals?.totalProgress) &&
                        getCharts('progress', 'percentage')}
                      {charts?.percentage?.charts?.length > 0 &&
                        getCharts('yourPerformance', 'percentage')}
                    </div>
                  )}
                  {charts?.VA && (
                    <div>
                      {(charts?.VA?.totals?.completionRate ||
                        charts?.VA?.totals?.totalProgress) &&
                        getCharts('progress', 'VA')}
                      {charts?.VA?.charts?.length > 0 &&
                        getCharts('yourPerformance', 'VA')}
                    </div>
                  )}
                </div>
              ) : (
                <div className="report-chart-content" />
              )}
            </BasicPortletWithFilters>
          </div>
        )}
      </div>
    </>
  );
}

TabPerformPerformance.propTypes = {
  t: PropTypes.func.isRequired,
  stats: PropTypes.shape({
    performance: PropTypes.shape(performanceProp.propType),
    charts: PropTypes.shape(chartsProp.propType),
  }).isRequired,
  certificates: PropTypes.arrayOf(PropTypes.shape(certificateProp.propType))
    .isRequired,
  customTrophies: PropTypes.arrayOf(PropTypes.shape(trophyProp.propType))
    .isRequired,
};

export default translate(TabPerformPerformance);
